// Phase G3 Stage 4 — onboarding draft store.
//
// Storybook persists each kid-step's input to Supabase as the kid
// walks the flow. Pencilwise's data path is different: the existing
// gate ladder collects everything into `progress` + `childrenStore`
// and only commits at the END of onboarding (the handoff page in
// the new multi-step flow).
//
// This store holds the in-progress draft across the 5 kid sub-routes.
// Handoff reads the full draft and calls the bridge hook
// `useFinishOnboarding()` to commit via pencilwise's existing data stores.
//
// P-AUTH-GATE-AND-ONBOARDING-DRAFT (QA-AUDIT Area 1 P1): the draft is now
// BACKED BY localStorage so a mid-onboarding refresh / closed tab no longer
// wipes everything (the in-memory `let state` singleton lost it on every
// reload). Persistence rules:
//   • Scoped PER auth user (key `pencilwise:onboardingDraft:{userId}`) — a
//     shared device never cross-reads a half-entered draft between accounts.
//   • 24h TTL + versioned envelope — see onboardingDraftStorage.ts for the
//     forward-compatibility contract (PR-F extends additively or bumps the
//     version to discard, never migrates).
//   • Rehydrated when the user binds (App calls setOnboardingDraftUser on
//     user-id change); persisted on every setDraft; CLEARED on successful
//     completion (useFinishOnboarding.finish → clearOnboardingDraft).
//   • All localStorage access is try/catch-wrapped: a private-mode / disabled
//     / corrupt store degrades to an empty in-memory draft (+ a Sentry
//     breadcrumb on a genuine parse failure) — onboarding still works, it just
//     won't survive a refresh. No throw ever reaches the flow.

import { useSyncExternalStore } from "react";
import type { ParentPriors } from "@/App/progress";
import {
  draftStorageKey,
  toPersisted,
  fromPersisted,
  DRAFT_TTL_MS,
} from "./onboardingDraftStorage";
import { captureDraftRehydrateError } from "./sentry";

export interface OnboardingDraft {
  name: string;
  age: number | null;
  locale: string;
  mascotId: string | null;
  // T1.1a: ability-slug priors ticked at /onboarding/kid/abilities. Carried
  // through the draft (like name/age/mascot) and committed at handoff via
  // finish() → handleOnboardingComplete, which spreads them into the new
  // child's progress.parentPriors. Kept on the draft (not written via
  // setProgress) because the child row doesn't exist until handoff and the
  // active child mid-onboarding may be a sibling.
  parentPriors: ParentPriors;
}

const EMPTY: OnboardingDraft = {
  name: "",
  age: null,
  locale: "en-US",
  mascotId: null,
  parentPriors: {},
};

let state: OnboardingDraft = { ...EMPTY };
// The auth user the draft is currently scoped to. null before sign-in /
// after sign-out → in-memory only (no key to persist under). Bound by App
// via setOnboardingDraftUser on every user-id change.
let currentUserId: string | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
function getSnapshot() {
  return state;
}

/** Persist the current state under the active user's key. No-op when no user
 *  is bound (can't scope a key) or storage is unavailable. */
function persist() {
  if (!currentUserId) return;
  try {
    localStorage.setItem(
      draftStorageKey(currentUserId),
      JSON.stringify(toPersisted(state, Date.now())),
    );
  } catch {
    // Private mode / quota / disabled storage. The draft stays in memory for
    // this sitting; it just won't survive a reload. Non-fatal by design.
  }
}

/** Load + validate the persisted draft for the active user into memory. Sets
 *  EMPTY when there's no user, nothing stored, or the record is
 *  expired/wrong-version (fromPersisted returns null → discard + remove). */
function rehydrate() {
  if (!currentUserId) {
    state = { ...EMPTY };
    return;
  }
  const key = draftStorageKey(currentUserId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      state = { ...EMPTY };
      return;
    }
    const next = fromPersisted(JSON.parse(raw), Date.now(), DRAFT_TTL_MS);
    if (next) {
      state = next;
    } else {
      // Expired or wrong-version → blow it away (no migration) so a stale
      // half-entered draft can't resurface.
      state = { ...EMPTY };
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    }
  } catch (err) {
    // A genuine parse/read failure (corrupt JSON). Surface it (breadcrumb +
    // console) — a swallowed corrupt draft would silently restart onboarding
    // with no trail — then degrade to empty.
    captureDraftRehydrateError("onboarding draft rehydrate failed", err, {
      key,
    });
    state = { ...EMPTY };
  }
}

/**
 * Bind the draft store to an auth user (called by App on user-id change).
 * Rehydrates from that user's persisted draft. Idempotent: re-binding the
 * same id is a no-op (won't thrash listeners or re-read storage).
 */
export function setOnboardingDraftUser(userId: string | null) {
  if (userId === currentUserId) return;
  currentUserId = userId;
  rehydrate();
  notify();
}

function setDraft(patch: Partial<OnboardingDraft>) {
  state = { ...state, ...patch };
  persist();
  notify();
}

function clearDraft() {
  state = { ...EMPTY };
  if (currentUserId) {
    try { localStorage.removeItem(draftStorageKey(currentUserId)); } catch { /* ignore */ }
  }
  notify();
}

/** Hook for steps to read + write the draft. */
export function useOnboardingDraft(): {
  draft: OnboardingDraft;
  setDraft: (patch: Partial<OnboardingDraft>) => void;
  clear: () => void;
} {
  const draft = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { draft, setDraft, clear: clearDraft };
}

/** Read-only snapshot — for the handoff bridge that doesn't need
 *  reactivity. */
export function readOnboardingDraft(): OnboardingDraft {
  return state;
}

/** Imperative clear for non-hook callers (useFinishOnboarding.finish on
 *  successful completion). Wipes the in-memory draft AND the persisted key. */
export function clearOnboardingDraft() {
  clearDraft();
}

/** Test-only seam: pure helpers + internal mutators exercised without a DOM.
 *  Not part of the app API. */
export const __test = {
  EMPTY,
  rehydrate,
  persist,
  setDraft,
  clearDraft,
  getState: () => state,
  getUserId: () => currentUserId,
  reset: () => {
    state = { ...EMPTY };
    currentUserId = null;
  },
};
